import React from 'react';
import { PALETTE } from './SiteLayout';

/**
 * Compliance & Trust band, sits site-wide just above the footer.
 * Uses "aligned" / "designed-for" language for frameworks without a
 * third-party certificate so claims stay substantiated.
 */
export default function ComplianceBand() {
  const items = [
    {
      label: 'Hosted in EU / Ireland, or on-premise',
      detail: 'Customer data resides on AWS Ireland (eu-west-1) and never leaves the EU. Local on-premise deployment available where policy requires it.',
      status: 'live',
    },
    {
      label: 'GDPR · Privacy Contact named',
      detail: 'Internal Data Protection Lead handles subject access requests. Owner is ADPO Ireland member.',
      status: 'live',
    },
    {
      label: 'MFA for every user',
      detail: 'TOTP multi-factor authentication across all roles, with rate limiting, brute-force lockout and reCAPTCHA bot protection on public forms.',
      status: 'live',
    },
    {
      label: 'ISO 27001 · aligned controls',
      detail: 'Security controls mapped to the ISO/IEC 27001:2022 Annex A framework, access management, encryption, logging, incident response.',
      status: 'live',
    },
    {
      label: 'NIS2 · supporting evidence',
      detail: 'Tenant isolation, MFA and tamper-evident audit trails give customers in NIS2 scope direct supporting evidence for their obligations.',
      status: 'live',
    },
    {
      label: 'Encryption · at rest & in transit',
      detail: 'TLS 1.3 in transit, industry-standard symmetric ciphers at rest, KMS-managed keys.',
      status: 'live',
    },
  ];

  return (
    <section
      data-testid="compliance-trust-band"
      style={{
        background: PALETTE.cream,
        borderTop: `1px solid ${PALETTE.border}`,
        borderBottom: `1px solid ${PALETTE.border}`,
        padding: '64px 32px',
      }}
    >
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 2fr',
          gap: 48,
          marginBottom: 32,
          alignItems: 'baseline',
        }} className="compliance-head">
          <div>
            <div style={{
              fontSize: 11,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              fontWeight: 700,
              color: PALETTE.magenta,
              marginBottom: 12,
            }}>Compliance & trust</div>
            <h3 style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 28,
              fontWeight: 700,
              color: PALETTE.navy,
              margin: 0,
              letterSpacing: '-0.015em',
              lineHeight: 1.1,
            }}>
              How we keep your<br/>
              <em style={{ fontStyle: 'italic', color: PALETTE.magenta, fontWeight: 500 }}>data and your audits safe.</em>
            </h3>
          </div>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: PALETTE.slate, margin: 0, paddingTop: 4 }}>
            Enterprise-grade controls as standard, encryption, MFA for every user, tenant isolation and tamper-evident audit trails, on EU cloud or your own servers. Privacy queries go to{' '}
            <a href="mailto:privacy@cloudvoro.com" style={{ color: PALETTE.navy, fontWeight: 600 }}>
              privacy@cloudvoro.com
            </a>
            . Sub-processor list at{' '}
            <a href="/legal/sub-processors" style={{ color: PALETTE.navy, fontWeight: 600 }}>/legal/sub-processors</a>
            . Full security posture at{' '}
            <a href="/site/security" style={{ color: PALETTE.navy, fontWeight: 600 }}>/site/security</a>.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 0,
          border: `1px solid ${PALETTE.border}`,
          background: '#fff',
          borderRadius: 8,
          overflow: 'hidden',
        }} className="compliance-grid">
          {items.map((it, i) => (
            <div
              key={it.label}
              style={{
                padding: '24px 24px',
                borderRight: (i + 1) % 3 === 0 ? 'none' : `1px solid ${PALETTE.border}`,
                borderBottom: i < 3 ? `1px solid ${PALETTE.border}` : 'none',
              }}
              className="compliance-cell"
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: it.status === 'live' ? '#059669' : '#D97706',
                  flexShrink: 0,
                }} />
                <span style={{
                  fontSize: 9.5,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  color: it.status === 'live' ? '#059669' : '#D97706',
                }}>
                  {it.status === 'live' ? 'Live' : 'In progress'}
                </span>
              </div>
              <div style={{
                fontSize: 14,
                fontWeight: 700,
                color: PALETTE.navy,
                lineHeight: 1.3,
                marginBottom: 6,
              }}>{it.label}</div>
              <div style={{ fontSize: 12, color: PALETTE.slate, lineHeight: 1.55 }}>
                {it.detail}
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 1100px) {
          .compliance-head { grid-template-columns: 1fr !important; gap: 24px !important; }
        }
        @media (max-width: 800px) {
          .compliance-grid { grid-template-columns: 1fr !important; }
          .compliance-cell { border-right: none !important; border-bottom: 1px solid ${PALETTE.border} !important; }
          .compliance-cell:last-child { border-bottom: none !important; }
        }
      `}</style>
    </section>
  );
}
